import React, { useEffect, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { useDebounce } from '@uidotdev/usehooks'
import { useInfiniteQuery } from '@tanstack/react-query'
import {
  addRoomMembers,
  chatRoomQueries,
  getHttpStatus,
} from '../../../../api/chat'
import { MemberCandidate } from '../../../../generated/api/chat-room-settings'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { theme } from '../../../../theme/theme'
import { Button, ButtonVariant } from '../../../common/Button'
import { Checkbox } from '../../../common/Checkbox'
import { InfiniteScroll } from '../../../common/InfiniteScroll'
import { Modal } from '../../../common/Modal'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { ModalFooter } from '../../../common/Modal/ModalFooter'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { SearchBar } from '../../../common/SearchBar'
import { ChipAvatar, chipFullName } from '../ChipAvatar'
import { useRoomHeaderMutation } from './useRoomHeaderMutation'

const SEARCH_DEBOUNCE_MS = 300

interface AddMembersModalProps {
  show: boolean
  close: () => void
  roomId: string
}

export const AddMembersModal = ({
  show,
  close,
  roomId,
}: AddMembersModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const modalRef = useRef<HTMLDivElement | null>(null)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS)

  useEffect(() => {
    if (show) return
    setSearch('')
    setSelected([])
  }, [show])

  const candidatesQuery = useInfiniteQuery({
    ...chatRoomQueries.memberCandidates(roomId, debouncedSearch.trim()),
    enabled: show,
  })
  const candidates =
    candidatesQuery.data?.pages.flatMap((page) => page.items) ?? []

  const { mutate: add, isPending } = useRoomHeaderMutation(
    (userIds: string[]) => addRoomMembers(roomId, { userIds }),
    {
      successMessage: translate(
        'chatSettings.flashMessage.addChatRoomMembersSuccess'
      ),
      errorMessage: (error) => {
        const serverMessage = isAxiosError(error)
          ? error.response?.data?.err
          : undefined
        return getHttpStatus(error) === 400 && typeof serverMessage === 'string'
          ? serverMessage
          : translate('chatSettings.flashMessage.addChatRoomMembersFailure')
      },
      onSuccess: close,
    }
  )

  const toggle = (userId: string): void => {
    setSelected((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    )
  }

  const renderCandidate = (candidate: MemberCandidate): React.ReactNode => (
    <div
      key={candidate.userId}
      role="button"
      tabIndex={0}
      className="flex cursor-pointer items-center justify-between rounded px-2 py-2 hover:bg-neutral-96"
      onClick={() => toggle(candidate.userId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          toggle(candidate.userId)
        }
      }}
      data-testid={`room-add-member-${candidate.userId}`}
    >
      <div className="flex items-center gap-4">
        <ChipAvatar chip={candidate} />
        <div className="flex flex-col">
          <p style={theme.textVariants.baseBold}>{chipFullName(candidate)}</p>
          {candidate.unitLabel && (
            <p style={theme.textVariants.caption} className="text-neutral-50">
              {candidate.unitLabel}
            </p>
          )}
        </div>
      </div>
      <div onClick={(e) => e.stopPropagation()} role="presentation">
        <Checkbox
          name={`room-add-member-${candidate.userId}-checkbox`}
          value={selected.includes(candidate.userId)}
          onChange={() => toggle(candidate.userId)}
        />
      </div>
    </div>
  )

  return (
    <Modal ref={modalRef} show={show}>
      {{
        header: (
          <ModalHeader onClose={close}>
            {translate('chatSettings.tables.titles.addMembers')}
          </ModalHeader>
        ),
        body: (
          <ModalBody className="max-h-96">
            <SearchBar
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder={translate('searchBar.placeholder.search')}
              widthClassName="w-full"
            />
            {!candidatesQuery.isLoading && candidates.length === 0 ? (
              <p style={theme.textVariants.base} className="text-neutral-50">
                {translate('chatSettings.form.noMembersFound')}
              </p>
            ) : (
              <InfiniteScroll
                onEndReached={() =>
                  candidatesQuery.fetchNextPage({ cancelRefetch: false })
                }
                isFetching={
                  candidatesQuery.isLoading ||
                  candidatesQuery.isFetchingNextPage
                }
                rootRef={modalRef}
                className="flex flex-col gap-1"
              >
                {candidates.map(renderCandidate)}
              </InfiniteScroll>
            )}
          </ModalBody>
        ),
        footer: (
          <ModalFooter>
            <Button
              type="button"
              label={translate('common.actions.cancel')}
              onClick={close}
              disabled={isPending}
            />
            <Button
              type="button"
              variant={ButtonVariant.PRIMARY}
              label={`${translate('common.actions.add')}${
                selected.length > 0 ? ` (${selected.length})` : ''
              }`}
              onClick={() => add(selected)}
              disabled={selected.length === 0}
              loading={isPending}
              data-testid="room-add-members-submit"
            />
          </ModalFooter>
        ),
      }}
    </Modal>
  )
}
